import { useCallback, useEffect, useRef, useState } from 'react';

import type { PhoneNumber } from '#src/app/shared/utils/phoneCollection';

import type { PhoneOptInEventHandlers } from '../events';
import type { PhoneOptInResult, PhoneOptInScreen } from '../types';

type UsePhoneOptInSequenceParams = Readonly<{
	/** The number on the account when the sequence opens. */
	initialPhoneNumber: PhoneNumber;
	events: PhoneOptInEventHandlers;
	/**
	 * Called whenever the last screen reports a result, which can happen more than once: a host
	 * that fails to complete the step leaves that screen mounted so the starter can try again.
	 */
	onFinished: (results: readonly PhoneOptInResult[]) => void;
}>;

type UsePhoneOptInSequenceReturn = Readonly<{
	screen: PhoneOptInScreen;
	/** The number to hand the current screen — carries over from the preceding screen's save. */
	phoneNumber: PhoneNumber;
	onScreenComplete: (result: PhoneOptInResult) => void;
}>;

/**
 * Runs the SMS ask then the journalist ask within a single host position, carrying the phone
 * number between them.
 *
 * For a host that shows both screens in one position. A surface that gives each screen its own
 * position — and so its own URL and view event — drives the screens directly instead, and a
 * surface that wants only one of them renders that screen on its own.
 */
export function usePhoneOptInSequence({
	initialPhoneNumber,
	events,
	onFinished,
}: UsePhoneOptInSequenceParams): UsePhoneOptInSequenceReturn {
	const [screen, setScreen] = useState<PhoneOptInScreen>('sms');
	const [phoneNumber, setPhoneNumber] = useState<PhoneNumber>(initialPhoneNumber);
	// A ref, not state: the invocation that appends the final result also has to read the whole set.
	const resultsRef = useRef<PhoneOptInResult[]>([]);

	// At most one view per screen, for the lifetime of the sequence.
	const viewedRef = useRef<Set<PhoneOptInScreen>>(new Set());
	useEffect(() => {
		if (viewedRef.current.has(screen)) return;
		viewedRef.current.add(screen);
		events.onScreenView(screen);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [screen]);

	const onScreenComplete = useCallback(
		(result: PhoneOptInResult) => {
			resultsRef.current = [...resultsRef.current, result];
			setPhoneNumber(result.phoneNumber);

			if (result.screen === 'sms') {
				setScreen('journalist');
				return;
			}

			onFinished(resultsRef.current);
		},
		[onFinished],
	);

	return { screen, phoneNumber, onScreenComplete };
}
